import { useEffect, useRef, useState } from 'react';
// Tab svg assets
import EconIcon from '../assets/general/economy.svg?react';
import EduIcon from '../assets/general/highschool-grad.svg?react';
import DemoIcon from '../assets/general/pop-blue.svg?react';
import HousingIcon from '../assets/general/Housing.svg?react';
import AccessibilityIcon from '../assets/general/Accessibility.svg?react';
import { tabColors } from '../utils/helpers';

interface TabMenuProps {
  tabs: string[];
  activeTab: string;
//   setCurrentTab: (tab: string) => void;
//   navigate: (path: string) => void;
  onTabClick: (tab: string) => void;
}

const tabSvgMap: { [key: string]: React.FC<React.SVGProps<SVGSVGElement>> } = {
  Education: EduIcon,
  People: DemoIcon,
  Economy: EconIcon,
  Accessibility: AccessibilityIcon,
  Housing: HousingIcon,
};

const getTabSvgMap = (id: string) => {
  return tabSvgMap[id] || null;
};

const TabMenu: React.FC<TabMenuProps> = ({
  tabs,
  activeTab,
  onTabClick
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [dynamicTab, setDynamicTab] = useState<string | null>(null);
    const [maxVisibleTabs, setMaxVisibleTabs] = useState(4);
    const menuRef = useRef<HTMLDivElement>(null);

    // Determine max visible tabs based on screen width
    const calculateMaxVisibleTabs = () => {
        const width = window.innerWidth;
        if (width > 380) return 5;
        if (width > 340) return 4;
        if (width > 300) return 3;
        if (width > 250) return 2;
        return 1;
    };

    // Set maxVisibleTabs on mount and resize
    useEffect(() => {
        const updateVisibleTabs = () => {
            setMaxVisibleTabs(calculateMaxVisibleTabs());
        };

        updateVisibleTabs();
        window.addEventListener('resize', updateVisibleTabs);
        return () => window.removeEventListener('resize', updateVisibleTabs);
    }, []);

    // Click-away logic for hamburger
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [menuOpen]);

    if (!tabs || tabs.length === 0) return null;

    // Build visible tab list
    const activeTabNormalized = activeTab?.toLowerCase();
    const baseVisible = tabs.slice(
        0,
        dynamicTab ? maxVisibleTabs - 1 : maxVisibleTabs
        );

    // Show active tab if space is very tight
    const showOnlyActive = maxVisibleTabs <= 2 && activeTab && tabs.includes(activeTab);

    let visibleTabs: string[];
    if (showOnlyActive) {
        visibleTabs = [activeTab];
    } else {
        visibleTabs = dynamicTab
        ? [...baseVisible.filter(t => t !== dynamicTab), dynamicTab]
        : baseVisible;
    }

    const visibleSet = new Set(visibleTabs);
    const extraTabs = tabs.filter(tab => !visibleSet.has(tab));

    const handleTabClick = (tabName: string, fromHamburger = false) => {
        if (fromHamburger) {
            setDynamicTab(tabName);
            setMenuOpen(false);
        }

        onTabClick(tabName); // <-- call the scroll function passed in from props
    };

    return (
        <div className="flex flex-wrap items-center justify-center gap-1 py-2 pb-[30px] no-print bg-port-blue relative">
        {visibleTabs.map((tabName) => {
            const isActive = activeTabNormalized === tabName.toLowerCase();
            const TabIcon = getTabSvgMap(tabName);
            const tabColor = tabColors[tabName] || "#21506F";

            return (
            <button
                key={tabName}
                className={`py-1 px-2 text-[10px] font-semibold rounded-md border transition-colors duration-150 group 
                ${isActive
                    ? `text-white border-[${tabColor}]`
                    : `text-white border-transparent hover:bg-[${tabColor}]/20`
                }`}
                style={{
                backgroundColor: isActive ? tabColor : undefined,
                borderColor: isActive ? tabColor : undefined,
                }}
                onClick={() => handleTabClick(tabName)}
            >
                <div className="flex flex-col items-center justify-center ">
                    {TabIcon && (
                        <TabIcon
                            className={`w-8 h-8 transition-colors duration-200 ${
                                isActive ? 'text-white' : 'text-white group-hover:text-white'
                            }`}
                        />
                    )}
                    <span>{tabName}</span>
                </div>
            </button>
            );
        })}

        {extraTabs.length > 0 && (
            <div className="relative" ref={menuRef}>
            <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="px-3 py-2 text-[10px] font-semibold rounded-md border bg-white text-white hover:bg-gray-600/50"
            >
                ☰
            </button>
            {menuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                {extraTabs.map((tabName) => (
                    <button
                    key={tabName}
                    onClick={() => handleTabClick(tabName, true)}
                    className="w-full px-4 py-2 text-xs text-left hover:bg-gray-100"
                    >
                    {tabName}
                    </button>
                ))}
                </div>
            )}
            </div>
        )}
        </div>
    );
};

export default TabMenu;